import React, { useEffect, useImperativeHandle, useState } from 'react';

import {
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material';

import DetailFormHeader from 'components/condition-list/DetailFormHeader';
import DetailMetaHeader from 'components/condition-list/DetailMetaHeader';
import FormSection from 'components/condition-list/FormSection';
import FinancialYearMonthSelect from 'components/form/FinancialYearMonthSelect';
import KatakanaTextField from 'components/form/KatakanaTextField';
import ReadOnlyField from 'components/form/ReadOnlyField';
import TextArea from 'components/form/TextArea';
import {
  MSG_INVALID_DATETIME_FORMAT,
  MSG_MAX_LENGTH,
  MSG_REQUIRED,
} from 'constant/ValidationMessages';
import { MasterTypeResult, getPartnerType } from 'stores/MasterTypeStoreAdapter';
import { PartnerResult, updatePartner } from 'stores/PartnerStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type PartnerUpdateFormHandle = {
  submit: () => void;
  isEditing: () => boolean;
};

type PartnerUpdateFormProps = {
  partner: PartnerResult;
  isEditing: boolean;
  onPartnerUpdated: (updatedPartner: PartnerResult) => void;
  onClose: () => void;
  onEditingChange: (editing: boolean) => void;
  setProgress: (v: boolean) => void;
  /** コンソールドロワー内で使う場合など、フォーム内蔵ヘッダーを非表示にする（既定: false） */
  hideHeader?: boolean;
};

/** yyyy-MM-dd'T'HH:mm:ss 形式の正規表現 */
const DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

const PartnerUpdateForm = React.forwardRef<PartnerUpdateFormHandle, PartnerUpdateFormProps>(
  (
    {
      partner,
      isEditing,
      onPartnerUpdated,
      onClose,
      onEditingChange,
      setProgress,
      hideHeader = false,
    },
    ref,
  ) => {
    const [partnerName, setPartnerName] = useState(partner.partnerName);
    const [partnerNameKana, setPartnerNameKana] = useState(partner.partnerNameKana);
    const [partnerTypeId, setPartnerTypeId] = useState(partner.partnerTypeId);
    const [corporateNumber, setCorporateNumber] = useState(partner.corporateNumber || '');
    const [invoiceNumber, setInvoiceNumber] = useState(partner.invoiceNumber || '');
    const [address, setAddress] = useState(partner.address || '');
    const [phoneNumber, setPhoneNumber] = useState(partner.phoneNumber || '');
    const [mailAddress, setMailAddress] = useState(partner.mailAddress || '');
    const [contactName, setContactName] = useState(partner.contactName || '');
    const [contractStartDate, setContractStartDate] = useState(partner.contractStartDate || '');
    const [contractEndDate, setContractEndDate] = useState(partner.contractEndDate || '');
    const [remarks, setRemarks] = useState(partner.remarks || '');

    const [partnerTypes, setPartnerTypes] = useState<MasterTypeResult[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
      getPartnerType()
        .then(setPartnerTypes)
        .catch(() => errorDefault('パートナー種別の取得に失敗しました'));
    }, []);

    useEffect(() => {
      setPartnerName(partner.partnerName);
      setPartnerNameKana(partner.partnerNameKana);
      setPartnerTypeId(partner.partnerTypeId);
      setCorporateNumber(partner.corporateNumber || '');
      setInvoiceNumber(partner.invoiceNumber || '');
      setAddress(partner.address || '');
      setPhoneNumber(partner.phoneNumber || '');
      setMailAddress(partner.mailAddress || '');
      setContactName(partner.contactName || '');
      setContractStartDate(partner.contractStartDate || '');
      setContractEndDate(partner.contractEndDate || '');
      setRemarks(partner.remarks || '');
      setErrors({});
      onEditingChange(false);
    }, [partner]);

    const handleBackToReadOnly = () => {
      setPartnerName(partner.partnerName);
      setPartnerNameKana(partner.partnerNameKana);
      setPartnerTypeId(partner.partnerTypeId);
      setCorporateNumber(partner.corporateNumber || '');
      setInvoiceNumber(partner.invoiceNumber || '');
      setAddress(partner.address || '');
      setPhoneNumber(partner.phoneNumber || '');
      setMailAddress(partner.mailAddress || '');
      setContactName(partner.contactName || '');
      setContractStartDate(partner.contractStartDate || '');
      setContractEndDate(partner.contractEndDate || '');
      setRemarks(partner.remarks || '');
      setErrors({});
      onEditingChange(false);
    };

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!partnerName) newErrors.partnerName = MSG_REQUIRED('パートナー名');
      else if (partnerName.length > 300)
        newErrors.partnerName = MSG_MAX_LENGTH('パートナー名', 300);

      if (!partnerNameKana) newErrors.partnerNameKana = MSG_REQUIRED('パートナー名（カナ）');
      else if (partnerNameKana.length > 300)
        newErrors.partnerNameKana = MSG_MAX_LENGTH('パートナー名（カナ）', 300);

      if (!partnerTypeId) newErrors.partnerTypeId = MSG_REQUIRED('パートナー種別');
      else if (partnerTypeId.length > 300)
        newErrors.partnerTypeId = MSG_MAX_LENGTH('パートナー種別', 300);

      if (!contractStartDate) newErrors.contractStartDate = MSG_REQUIRED('契約開始日');
      else if (!DATETIME_REGEX.test(contractStartDate))
        newErrors.contractStartDate = MSG_INVALID_DATETIME_FORMAT('契約開始日');

      if (contractEndDate && !DATETIME_REGEX.test(contractEndDate))
        newErrors.contractEndDate = MSG_INVALID_DATETIME_FORMAT('契約終了日');

      if (corporateNumber && corporateNumber.length > 20)
        newErrors.corporateNumber = MSG_MAX_LENGTH('法人番号', 20);

      if (invoiceNumber && invoiceNumber.length > 20)
        newErrors.invoiceNumber = MSG_MAX_LENGTH('インボイス番号', 20);

      if (address && address.length > 500) newErrors.address = MSG_MAX_LENGTH('住所', 500);

      if (phoneNumber && phoneNumber.length > 20)
        newErrors.phoneNumber = MSG_MAX_LENGTH('電話番号', 20);

      if (mailAddress && mailAddress.length > 300)
        newErrors.mailAddress = MSG_MAX_LENGTH('メールアドレス', 300);

      if (contactName && contactName.length > 300)
        newErrors.contactName = MSG_MAX_LENGTH('担当者名', 300);

      if (remarks && remarks.length > 10000) newErrors.remarks = MSG_MAX_LENGTH('備考', 10000);

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
      if (!validate()) return;

      setProgress(true);
      updatePartner({
        partnerId: partner.partnerId,
        partnerName,
        partnerNameKana,
        partnerTypeId,
        ...(corporateNumber && { corporateNumber }),
        ...(invoiceNumber && { invoiceNumber }),
        ...(address && { address }),
        ...(phoneNumber && { phoneNumber }),
        ...(mailAddress && { mailAddress }),
        ...(contactName && { contactName }),
        contractStartDate,
        ...(contractEndDate && { contractEndDate }),
        ...(remarks && { remarks }),
      })
        .then(() => {
          successDefault('パートナー情報を更新しました');
          const updatedPartner: PartnerResult = {
            ...partner,
            partnerName,
            partnerNameKana,
            partnerTypeId,
            corporateNumber: corporateNumber || undefined,
            invoiceNumber: invoiceNumber || undefined,
            address: address || undefined,
            phoneNumber: phoneNumber || undefined,
            mailAddress: mailAddress || undefined,
            contactName: contactName || undefined,
            contractStartDate,
            contractEndDate: contractEndDate || undefined,
            remarks: remarks || undefined,
          };
          onPartnerUpdated(updatedPartner);
          onEditingChange(false);
        })
        .catch((e: Error) => {
          errorDefault('パートナー情報の更新に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    useImperativeHandle(ref, () => ({ submit: handleSave, isEditing: () => isEditing }));

    const getPartnerTypeLabel = (typeId: string): string => {
      const found = partnerTypes.find((t) => t.typeCode === typeId);
      return found ? found.typeLabel : typeId;
    };

    const isContractActive =
      !partner.contractEndDate || new Date(partner.contractEndDate) >= new Date();

    /** 共通ヘッダー */
    const renderHeader = (title: string, subtitle: string, onBack: () => void) => (
      <DetailFormHeader
        title={title}
        subtitle={subtitle}
        onBack={onBack}
        copyId={partner.partnerId}
        status={
          <Chip
            label={isContractActive ? '契約中' : '契約終了'}
            size="small"
            color={isContractActive ? 'primary' : 'default'}
            sx={{ fontWeight: 700 }}
          />
        }
        meta={`作成者: ${partner.createdByName} 作成日: ${formatDate(partner.createdAt)}`}
      />
    );

    // --- 参照モード ---
    if (!isEditing) {
      return (
        <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {!hideHeader && renderHeader('パートナー詳細', 'パートナー情報を確認します', onClose)}

          <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
            {hideHeader && (
              <DetailMetaHeader
                id={partner.partnerId}
                metaItems={[
                  {
                    label: '作成者',
                    value: `${partner.createdByName} (${formatDate(partner.createdAt)})`,
                  },
                ]}
              />
            )}
            <FormSection title="基本情報">
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField label="パートナー名" value={partner.partnerName} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="パートナー名（カナ）" value={partner.partnerNameKana} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField
                    label="パートナー種別"
                    value={getPartnerTypeLabel(partner.partnerTypeId)}
                  />
                </Grid>
              </Grid>
            </FormSection>

            <Divider />

            <FormSection title="契約期間">
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField
                    label="契約開始日"
                    value={partner.contractStartDate ? formatDate(partner.contractStartDate) : '-'}
                  />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField
                    label="契約終了日"
                    value={partner.contractEndDate ? formatDate(partner.contractEndDate) : '-'}
                  />
                </Grid>
              </Grid>
            </FormSection>

            <Divider />

            <FormSection title="法人情報">
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField label="法人番号" value={partner.corporateNumber || ''} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="インボイス番号" value={partner.invoiceNumber || ''} />
                </Grid>
                <Grid size={12}>
                  <ReadOnlyField label="住所" value={partner.address || ''} />
                </Grid>
              </Grid>
            </FormSection>

            <Divider />

            <FormSection title="連絡先">
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField label="担当者名" value={partner.contactName || ''} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="電話番号" value={partner.phoneNumber || ''} />
                </Grid>
                <Grid size={12}>
                  <ReadOnlyField label="メールアドレス" value={partner.mailAddress || ''} />
                </Grid>
              </Grid>
            </FormSection>

            <Divider />

            {/* 備考 */}
            <ReadOnlyField label="備考" value={partner.remarks || ''} />
          </Stack>
        </Stack>
      );
    }

    // --- 編集モード ---
    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {!hideHeader &&
          renderHeader(
            'パートナー詳細（編集中）',
            'パートナー情報を編集します',
            handleBackToReadOnly,
          )}

        <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
          {hideHeader && (
            <DetailMetaHeader
              id={partner.partnerId}
              metaItems={[
                {
                  label: '作成者',
                  value: `${partner.createdByName} (${formatDate(partner.createdAt)})`,
                },
              ]}
            />
          )}
          <FormSection title="基本情報">
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="パートナー名"
                  value={partnerName}
                  onChange={(e) => setPartnerName(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.partnerName}
                  helperText={errors.partnerName}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <KatakanaTextField
                  label="パートナー名（カナ）"
                  value={partnerNameKana}
                  onChange={(v) => setPartnerNameKana(v)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.partnerNameKana}
                  helperText={errors.partnerNameKana}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                />
              </Grid>
              <Grid size={12}>
                <FormControl required error={!!errors.partnerTypeId}>
                  <FormLabel sx={{ mb: 0.5 }}>パートナー種別</FormLabel>
                  <RadioGroup
                    row
                    value={partnerTypeId}
                    onChange={(e) => setPartnerTypeId(e.target.value)}
                  >
                    {partnerTypes.map((t) => (
                      <FormControlLabel
                        key={t.typeCode}
                        value={t.typeCode}
                        control={<Radio size="small" />}
                        label={t.typeLabel}
                      />
                    ))}
                  </RadioGroup>
                  {errors.partnerTypeId && <FormHelperText>{errors.partnerTypeId}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          </FormSection>

          <Divider />

          <FormSection title="契約期間">
            <Grid container spacing={2}>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="start"
                  value={contractStartDate}
                  onChange={setContractStartDate}
                  label="契約開始日"
                  required
                  error={!!errors.contractStartDate}
                  helperText={errors.contractStartDate}
                />
              </Grid>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="end"
                  value={contractEndDate}
                  onChange={setContractEndDate}
                  label="契約終了日"
                  error={!!errors.contractEndDate}
                  helperText={errors.contractEndDate}
                />
              </Grid>
            </Grid>
          </FormSection>

          <Divider />

          <FormSection title="法人情報">
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="法人番号"
                  value={corporateNumber}
                  onChange={(e) => setCorporateNumber(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.corporateNumber}
                  helperText={errors.corporateNumber}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="インボイス番号"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.invoiceNumber}
                  helperText={errors.invoiceNumber}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="住所"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.address}
                  helperText={errors.address}
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </FormSection>

          <Divider />

          <FormSection title="連絡先">
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="担当者名"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.contactName}
                  helperText={errors.contactName}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="電話番号"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="メールアドレス"
                  value={mailAddress}
                  onChange={(e) => setMailAddress(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.mailAddress}
                  helperText={errors.mailAddress}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </FormSection>

          <Divider />

          {/* 備考 */}
          <TextArea
            label="備考"
            value={remarks}
            onChange={setRemarks}
            maxLength={10000}
            rows={6}
            fullWidth
            size="small"
            errorMessage={errors.remarks}
          />
        </Stack>
      </Stack>
    );
  },
);

PartnerUpdateForm.displayName = 'PartnerUpdateForm';

export default PartnerUpdateForm;
